import type { Order } from "./types";

/**
 * Client-side CSV export for the orders list.
 *
 * The async server-side export pipeline (`POST /v1/orders/export`) is a stub this
 * phase, so the UI generates the file in the browser from the orders list — mirroring
 * the existing report exports (inventory diff, inbound). CSV headers are file-system
 * data labels, not user-facing copy, so they live here rather than in i18n.
 */

interface ExportColumn {
  /** Matches the column keys in ExportConfig. */
  key: string;
  header: string;
  value: (o: Order) => string;
}

/** Canonical column order — selection toggles membership, never order. */
export const EXPORT_COLUMNS: ExportColumn[] = [
  { key: "th_order", header: "Order ID", value: (o) => o.sale },
  { key: "th_marketplace", header: "Marketplace", value: (o) => o.mkp },
  { key: "th_created", header: "Created", value: (o) => o.created },
  { key: "th_customer", header: "Customer", value: (o) => o.cust },
  { key: "th_total", header: "Total", value: (o) => o.money.total.toFixed(2) },
  { key: "th_currency", header: "Currency", value: (o) => o.currency ?? "" },
  { key: "th_status", header: "Status", value: (o) => o.status },
  { key: "th_payment", header: "Payment", value: (o) => o.pay },
  { key: "oi_seller", header: "Seller ID", value: (o) => o.sellerId },
  { key: "oi_seller_name", header: "Seller Name", value: (o) => o.seller },
  { key: "lbl_tracking", header: "Tracking", value: (o) => o.tracking ?? "" },
];

/** RFC 4180 quoting: wrap and double-up quotes when the value holds comma/quote/newline. */
function escapeCsv(value: string): string {
  if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

/**
 * Serialise orders to CSV (header + one row per order).
 * `columns` selects which canonical columns appear; an empty selection exports all.
 */
export function ordersToCsv(orders: Order[], columns: string[]): string {
  const selected = EXPORT_COLUMNS.filter((c) => columns.includes(c.key));
  const cols = selected.length > 0 ? selected : EXPORT_COLUMNS;
  const header = cols.map((c) => c.header).join(",");
  const rows = orders.map((o) => cols.map((c) => escapeCsv(c.value(o))).join(","));
  return [header, ...rows].join("\n");
}

/**
 * Keep orders whose `created` date falls within the inclusive [from, to] range.
 * Bounds are "YYYY-MM-DD"; an empty bound is open-ended. With no bounds, every
 * order is kept. Once any bound is set, orders without a parseable date are dropped.
 */
export function filterByDateRange(orders: Order[], from: string, to: string): Order[] {
  if (!from && !to) return orders;
  return orders.filter((o) => {
    const date = o.created.slice(0, 10);
    if (!date) return false;
    if (from && date < from) return false;
    if (to && date > to) return false;
    return true;
  });
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** Local calendar date as YYYY-MM-DD. */
function ymd(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * Build a descriptive download filename. A full range becomes
 * `orders_<from>_to_<to>.csv`; a single open bound is labelled; with no range
 * the file is stamped with the generation date (so exports don't all collide
 * on one generic name).
 */
export function exportFileName(from: string, to: string, now: Date = new Date()): string {
  if (from && to) return `orders_${from}_to_${to}.csv`;
  if (from) return `orders_from_${from}.csv`;
  if (to) return `orders_until_${to}.csv`;
  return `orders_${ymd(now)}.csv`;
}

/** Trigger a browser download of CSV text. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
